import {
    NIGHTLIFE_EVENTBRITE_CATEGORY,
    PARTY_EVENTBRITE_FORMAT,
} from '@eventbrite/categories';
import { CoreUser } from '@eventbrite/context-gen';
import { gettext } from '@eventbrite/i18n';
import loadable from '@loadable/component';
import { Location } from 'history';
import React from 'react';
import { DateRange } from '../../../constants/dates';
import { PaidEventsCarousel } from '../../../containers/PaidEventsCarousel';
import { categoryCodeToReadableTag } from '../../../utils/categories';
import { PridePageTouchpoint } from '../components/PridePageTouchpoint';
import {
    COMPONENTS_LABELS,
    COMPONENTS_ORDER,
} from '../constants/categoryCustomConfig';
import { CategoryBrowseDynamicFormatEvents } from '../containers/CategoryBrowseDynamicFormatEvents';
import { CategoryBrowseEvents } from '../containers/CategoryBrowseEvents';
import { CategoryBrowseRelatedCategories } from '../containers/CategoryBrowseRelatedCategories';
import { TrendingTagEvents } from '../containers/TrendingTagEvents';
import { CATEGORY_BROWSE_PLACEMENT } from './analytics';

const CategoryBrowseOrganizers = loadable(
    () => import('../containers/CategoryBrowseOrganizers'),
);
const CategoryBrowseOrganizerTags = loadable(
    () => import('../containers/CategoryBrowseOrganizerTags'),
);

const CategoryBrowseNearbyCities = loadable(
    () => import('../containers/CategoryBrowseNearbyCities'),
);

const CategoryBrowsePopularCategories = loadable(
    () => import('../containers/CategoryBrowsePopularCategories'),
    {
        resolveComponent: (components: {
            CategoryBrowsePopularCategories: React.FunctionComponent;
        }) => components.CategoryBrowsePopularCategories,
    },
);

const CategoryBrowsePopularFormats = loadable(
    () => import('../containers/CategoryBrowsePopularFormats'),
);

const CategoryBrowsePopularTimes = loadable(
    () => import('../containers/CategoryBrowsePopularTimes'),
);

interface CarouselSequenceProps {
    activeCategory: string;
    date: string | undefined;
    customDate: DateRange | undefined;
    location: Location<string> | undefined;
    locale?: string;
    isMobile?: boolean;
    user: CoreUser | undefined;
    isBotRequest: boolean;
    placeId: string;
    currentPlace: string;
    enableSeasonalModule: boolean;
}

export const CarouselSequence = (props: CarouselSequenceProps) => {
    const CATEGORY_BROWSE_COMPONENTS: {
        [key: string]: any;
    } = {
        [COMPONENTS_LABELS.categoryTab]: {
            component: CategoryBrowseRelatedCategories,
        },
        [COMPONENTS_LABELS.mostPopular]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    page_size: 25,
                },
                bucketName: CATEGORY_BROWSE_PLACEMENT,
                bucketKey: 'most_popular',
                bucketPosition: '1',
                renderEmptyState: true,
                adSlots: [1, 4],
            },
        },
        [COMPONENTS_LABELS.weekend]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    page_size: 25,
                    dates: ['this_weekend'],
                    aggs: {},
                },
                bucketName: CATEGORY_BROWSE_PLACEMENT,
                bucketKey: 'this_weekend',
                title: gettext('This weekend'),
                bucketPosition: '2',
                adSlots: [1],
            },
            renderCondition:
                props.date === 'current_future' && !props.customDate,
        },
        [COMPONENTS_LABELS.organizerToFollow]: {
            component: CategoryBrowseOrganizers,
            params: {
                user: props.user,
                location: props.location,
            },
        },
        [COMPONENTS_LABELS.nightlife]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    page_size: 25,
                    start_hour_range: {
                        end_hour: 24,
                        start_hour: 20,
                    },
                },
                title: gettext('Nightlife'),
                bucketName: CATEGORY_BROWSE_PLACEMENT,
                bucketKey: NIGHTLIFE_EVENTBRITE_CATEGORY,
                bucketPosition: '1',
            },
        },
        [COMPONENTS_LABELS.parties]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: {
                    tags: [PARTY_EVENTBRITE_FORMAT, props.activeCategory],
                    page_size: 25,
                },
                title: gettext('Parties'),
                bucketName: CATEGORY_BROWSE_PLACEMENT,
                bucketKey:
                    categoryCodeToReadableTag(PARTY_EVENTBRITE_FORMAT) ||
                    PARTY_EVENTBRITE_FORMAT,
                bucketPosition: '1',
            },
        },
        [COMPONENTS_LABELS.pridePageTouchpoint]: {
            component: PridePageTouchpoint,
            params: {
                currentPlace: props.currentPlace,
                placeId: props.placeId,
            },
        },
        [COMPONENTS_LABELS.budgetFriendly]: {
            component: PaidEventsCarousel,
            params: {
                isAuthenticated: props.user?.isAuthenticated,
                currentPlace: props.currentPlace,
                placeId: props.placeId,
                locale: props.locale,
                page: props.activeCategory,
                isMobile: props.isMobile,
            },
        },
        [COMPONENTS_LABELS.dynamicEvents0]: {
            component: CategoryBrowseDynamicFormatEvents,
            params: {
                bucketName: CATEGORY_BROWSE_PLACEMENT,
                bucketNumber: 0,
                bucketPosition: '5',
            },
        },
        [COMPONENTS_LABELS.free]: {
            component: CategoryBrowseEvents,
            params: {
                queryOverload: { page_size: 25, price: 'free', aggs: {} },
                title: gettext('Free'),
                bucketName: CATEGORY_BROWSE_PLACEMENT,
                bucketKey: 'free',
                bucketPosition: '6',
                adSlots: [1],
                forceRenderHeader: props.isBotRequest,
            },
        },
        [COMPONENTS_LABELS.trendingTags]: {
            component: TrendingTagEvents,
            params: {
                title: gettext('Trending Tags'),
                bucketName: CATEGORY_BROWSE_PLACEMENT,
                bucketKey: 'trending_tag_events',
                bucketPosition: '7',
                placeId: props.placeId,
            },
        },
        [COMPONENTS_LABELS.dynamicEvents1]: {
            component: CategoryBrowseDynamicFormatEvents,
            params: {
                bucketName: CATEGORY_BROWSE_PLACEMENT,
                bucketNumber: 1,
                bucketPosition: '8',
            },
        },
        [COMPONENTS_LABELS.dynamicEvents2]: {
            component: CategoryBrowseDynamicFormatEvents,
            params: {
                bucketName: CATEGORY_BROWSE_PLACEMENT,
                bucketNumber: 2,
                bucketPosition: '9',
            },
        },
        [COMPONENTS_LABELS.popularFormats]: {
            component: CategoryBrowsePopularFormats,
        },
        [COMPONENTS_LABELS.popularTimes]: {
            component: CategoryBrowsePopularTimes,
        },
        [COMPONENTS_LABELS.popularCategories]: {
            component: CategoryBrowsePopularCategories,
            params: { enableSeasonalModule: props.enableSeasonalModule },
        },
        [COMPONENTS_LABELS.nearbyCities]: {
            component: CategoryBrowseNearbyCities,
        },
        [COMPONENTS_LABELS.OrganizersTags]: {
            component: CategoryBrowseOrganizerTags,
            key: COMPONENTS_LABELS.OrganizersTags,
        },
    };

    const renderOrder =
        COMPONENTS_ORDER[props.activeCategory] || COMPONENTS_ORDER.default;

    const components: any[] = [];
    renderOrder.forEach((componentKey) => {
        const componentData = CATEGORY_BROWSE_COMPONENTS[componentKey];
        if (componentData) {
            components.push(componentData);
        }
    });
    return components;
};
